import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "flowbite-react";
import Button from "../../../../components/button/button";
import TextBox from "../../../../components/forms/text-box";
import SelectInput from "../../../../components/forms/select";
import Loader from "../../../../components/form-loader/form-loader";
import Message from "../../../../components/toast-message";
import { createAccountUser } from "../../../../services/account/account-users-service";

const ROLE_OPTIONS = [
  { label: "Admin", value: "admin" },
  { label: "Editor", value: "editor" },
];

const INITIAL_FORM = {
  name: "",
  email: "",
  password: "",
  role: ROLE_OPTIONS[1].value,
};

const INITIAL_ERRORS = {
  name: false,
  email: false,
  password: false,
  role: false,
};

export default function AddUserDialog({ open, onCompletion, onCancel }) {
  const rootRef = useRef(null);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState({ ...INITIAL_ERRORS });
  const [formState, setFormState] = useState({
    processing: false,
    failed: false,
    success: false,
    errorMessage: "",
  });

  const roleOptions = useMemo(() => ROLE_OPTIONS, []);

  useEffect(() => {
    if (open) {
      setFormData({ ...INITIAL_FORM });
      setErrors({ ...INITIAL_ERRORS });
      setFormState({
        processing: false,
        failed: false,
        success: false,
        errorMessage: "",
      });
    }
  }, [open]);

  useEffect(() => {
    if (!formState.success) return;
    const timer = setTimeout(() => {
      onCompletion?.();
    }, 2000);
    return () => clearTimeout(timer);
  }, [formState.success, onCompletion]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validateForm = useCallback(() => {
    const nextErrors = {
      name: !formData.name.trim(),
      email: !formData.email.trim(),
      password: !formData.password.trim(),
      role: !formData.role,
    };
    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  }, [formData]);

  const updateFormState = (processing, failed, errorMessage = "") => {
    setFormState((prev) => ({ ...prev, processing, failed, errorMessage }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formState.processing) return;
    if (!validateForm()) return;

    updateFormState(true, false);
    const { response, status } = await createAccountUser(formData);
    if (status >= 200 && status < 300) {
      setFormState({
        processing: false,
        failed: false,
        success: true,
        errorMessage: "",
      });
    } else {
      setFormState({
        processing: false,
        failed: true,
        success: false,
        errorMessage: response,
      });
    }
  };

  const handleClose = () => {
    if (formState.processing) return;
    if (formState.success) {
      onCompletion?.();
    } else {
      onCancel?.();
    }
  };

  return (
    <div ref={rootRef}>
      <Modal
        show={open}
        size="md"
        popup
        onClose={handleClose}
        root={rootRef.current ?? undefined}
      >
        <Modal.Header />
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                Invite User
              </h3>

              {formState.processing && (
                <div className="p-2">
                  <Loader />
                </div>
              )}

              {formState.failed && (
                <div className="p-2">
                  <Message text={formState.errorMessage} type="failure" />
                </div>
              )}

              {formState.success && (
                <div className="p-2">
                  <Message type="success" text="User invited successfully." />
                </div>
              )}

              <TextBox
                rootClass="w-full"
                label="Name"
                type="text"
                name="name"
                id="name"
                placeholder="Jane Doe"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
              />

              <TextBox
                rootClass="w-full"
                label="Email"
                type="email"
                name="email"
                id="email"
                placeholder="jane@company.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
              />

              <TextBox
                rootClass="w-full"
                label="Password"
                type="password"
                name="password"
                id="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
              />

              <SelectInput
                rootClass="w-full"
                label="Role"
                id="role"
                name="role"
                options={roleOptions}
                value={formData.role}
                onChange={handleChange}
                error={errors.role}
              />

              <div className="w-full flex flex-row gap-2 justify-end">
                {formState.success ? (
                  <Button
                    variant="button"
                    type="button"
                    text="Done"
                    onClick={handleClose}
                  />
                ) : (
                  <>
                    <Button
                      variant="button"
                      color="default"
                      type="button"
                      text="Cancel"
                      onClick={handleClose}
                      disabled={formState.processing}
                    />
                    <Button
                      variant="button"
                      type="submit"
                      text="Invite User"
                      disabled={formState.processing}
                    />
                  </>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
